import { useMemo } from "react";
import isEqual from "lodash/isEqual";

import {
  DEFAULT_NOTICE_SEMANTIC_COLUMN_FIELDS,
  normalizeNoticeSemanticColumnFields,
} from "components/notices_v2/noticeSemanticColumns";
import {
  DEFAULT_NOTICE_SEMANTIC_SORT,
  type NoticeSemanticSort,
} from "components/notices_v2/noticeSemanticSort";
import { DEFAULT_FILTERS, NoticeV2Filters, stageUnionToEnum } from "components/notices_v2/utils";
import { NoticeSemanticColumnField, SavedNoticeSemanticView } from "lib/types/savedViews";

export type NoticesV2ViewStatus = undefined | { status: "saved" | "unsaved"; message: string };

export type UseNoticesV2ViewStatusArgs = {
  selectedView?: SavedNoticeSemanticView;
  filters: NoticeV2Filters;
  columnFields: NoticeSemanticColumnField[];
  semanticSort: NoticeSemanticSort;
};

export function useNoticesV2ViewStatus({
  selectedView,
  filters,
  columnFields,
  semanticSort,
}: UseNoticesV2ViewStatusArgs) {
  const areSettingsEqualToDefault = useMemo(
    () =>
      isEqual(filters, DEFAULT_FILTERS) &&
      isEqual(columnFields, DEFAULT_NOTICE_SEMANTIC_COLUMN_FIELDS) &&
      isEqual(semanticSort, DEFAULT_NOTICE_SEMANTIC_SORT),
    [filters, columnFields, semanticSort],
  );

  const haveSettingsChanged = useMemo(() => {
    if (!selectedView) return false;
    const savedFilters: NoticeV2Filters = {
      ...selectedView.view.filterSettings,
      stage: stageUnionToEnum(selectedView.view.filterSettings.stage),
    };
    const savedColumnFields = normalizeNoticeSemanticColumnFields(
      selectedView.view.tableSettings.columns.map((c) => c.field),
    );
    return (
      !isEqual(filters, savedFilters) ||
      !isEqual(columnFields, savedColumnFields) ||
      !isEqual(semanticSort, selectedView.view.sortOrder)
    );
  }, [selectedView, filters, columnFields, semanticSort]);

  const viewStatus = useMemo((): NoticesV2ViewStatus => {
    if (selectedView) {
      if (haveSettingsChanged) {
        return { status: "unsaved", message: "Unsaved" };
      }
      return { status: "saved", message: "Saved" };
    }

    if (!areSettingsEqualToDefault) {
      return { status: "unsaved", message: "Unsaved" };
    }

    return undefined;
  }, [areSettingsEqualToDefault, haveSettingsChanged, selectedView]);

  return {
    areSettingsEqualToDefault,
    haveSettingsChanged,
    viewStatus,
  };
}
